// i18n
import { translateInComponent, translate } from 'infras/i18n';
import translations from './i18n.json';

import {
  SyntheticEvent,
  useEffect,
  useState,
  useContext,
  useMemo,
  useRef,
  FormEvent,
} from 'react';
import { useRouter } from 'next/router';

import {
  currentTimeZone as getCurrentTimeZone,
  DateTime,
  getDateTime,
  formatToSeconds,
} from 'infras/datetime';
import { UserListItem, ViewerItem } from 'presentations/api/schema/user';
import { apiPath, EventCreateViewType } from 'presentations/routes/path';
import { post, patch, get } from 'infras/rest_client';
import { ErrorList } from 'domains/models';
import Workflow from 'domains/models/workflow';
import { LinkType } from 'domains/models/routine/linkType';
import { useRequest } from 'presentations/views/hooks/request';
import {
  ScheduleFormData,
  convertToScheduleFormData,
  convertToUtcSchedules,
} from 'presentations/views/helpers/routine';
import { defaultScheduleItem } from 'infras/models/routine/data';
import {
  DashboardPortalContext,
  setErrorMessage,
} from 'presentations/reducers/DashboardPortal';
import { getErrorMessage } from 'presentations/views/helpers/error';
import { TimeZoneListItem } from 'presentations/api/schema/time_zone';
import { TeamUserListItem } from 'presentations/api/schema/team_user';
import { LocationType } from 'domains/models/event_type/locationType';
import { EventTypeUserListItem } from 'presentations/api/schema/event_type_user';
import { Role, RoleText } from 'domains/models/event_type_user/role';
import {
  UserType,
  UserTypeText,
} from 'domains/models/event_type_user/user_type';
import { AutoCompleteOption } from 'presentations/views/components/atoms/AutoCompleteTextField';
import { DisplayType } from 'domains/models/event_type/displayType';
import {
  EventDetailItem,
  EventUpdateField,
} from 'presentations/api/schema/event';
import { isEmail } from 'infras/validator';
import { UserAppItem } from 'presentations/api/schema/account';
import { VisibilityType } from 'domains/models/event/visibilityType';
import { useRoutineScheduleForm } from 'presentations/views/components/organisms/RoutineScheduleForm/hooks';
import { Transparency } from 'domains/models/event/transparency';
import Event from 'domains/models/event';
import { Behavior } from 'domains/models/event/behavior';
import {
  CalendarContext,
  setCreatingEvent,
} from '../../atoms/Calendar/reducer/CalendarContext';
import { SourceType } from 'domains/models/event_source/sourceType';
import { BookingUpdateBody } from 'presentations/api/schema/public/booking';
import {
  convertBehaviorToText,
  convertDisplayTypeToText,
  convertLocationTypeToText,
  convertRoleToText,
  convertPriorityToText,
  convertTransparencyToText,
  convertVisibilityTypeToText,
} from 'presentations/api/helpers/event';
import {
  ScheduleCreateBody,
  ScheduleUpdateBody,
} from 'presentations/api/schema/public/schedule';

interface Props {
  viewer: ViewerItem;
  routineEvent: EventDetailItem | null;
  teamUsers?: TeamUserListItem[];
  onCancel?: any;
  onCreateEvent?: (
    newEvent: Event,
    selectedTeamUsers: (string | EventTypeUserListItem)[],
  ) => void;
  userApp: UserAppItem | null;
  isPopoverOpen?: boolean;
  behavior?: Behavior;
  // viewType?: EventCreateViewType;
  onUpdateEvent?: any;
}

const defaultDuration = 60;
const minuteStep = 15;

export const useRoutineForm = (props: Props) => {
  const t = translateInComponent(translations);
  const currentTimeZone = getCurrentTimeZone();

  const { state: dashboardState, dispatch: dashboardDispatch } = useContext(
    DashboardPortalContext,
  );
  const { state, dispatch } = useContext(CalendarContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEditReady, setIsEditReady] = useState<boolean>(false);
  const [hasWorkflows, setHasWorkflows] = useState<boolean>(false);

  const [errors, setErrors] = useState<ErrorList>({});

  const nameInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [slackChannel, setSlackChannel] = useState<string | null>(null);
  const [priority, setPriority] = useState<number | null>(null);
  const [tag, setTag] = useState<number | null>(null);
  const [visibility, setVisibility] = useState<DisplayType>(DisplayType.Public);

  const [isRemind, setIsRemind] = useState<boolean>(true);
  const [duration, setDuration] = useState<number>(defaultDuration);
  const [customLink, setCustomLink] = useState<string | null>(null);
  const [linkType, setLinkType] = useState<LinkType>(LinkType.Default);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [sourceType, setSourceType] = useState<SourceType | null>(null);

  const [timezone, setTimezone] = useState<string>(currentTimeZone);
  const [description, setDescription] = useState<string | null>(null);
  const [transparency, setTransparency] = useState<Transparency>(
    props.behavior === Behavior.Work
      ? Transparency.Transparent
      : Transparency.Opaque,
  );

  const [isAllday, setIsAllday] = useState<boolean>(
    state.creatingEvent?.isAllDay || false,
  );
  const [behavior, setBehavior] = useState<Behavior>(
    props.behavior || Behavior.Event,
  );
  const [startedAt, setStartedAt] = useState<DateTime | null>(
    state.creatingEvent
      ? getDateTime(state.creatingEvent.startedAt, state.timeZone)
      : null,
  );
  const [endedAt, setEndedAt] = useState<DateTime | null>(
    state.creatingEvent
      ? getDateTime(state.creatingEvent.endedAt, state.timeZone)
      : null,
  );
  const [lastStartedAt, setLastStartedAt] = useState<DateTime | null>(null);
  const [lastEndedAt, setLastEndedAt] = useState<DateTime | null>(null);
  const [isRepeat, setIsRepeat] = useState<boolean>(false);
  const [endDate, setEndDate] = useState<number>(0);
  const [endDateSpecified, setEndDateSpecified] = useState<DateTime | null>(
    null,
  );

  const [locationType, setLocationType] = useState<
    LocationType | 'unspecified'
  >('unspecified');
  const [locationAddress, setLocationAddress] = useState<string | null>(null);
  const [locationAccess, setLocationAccess] = useState<string | null>(null);
  const [locationPhoneNumber, setLocationPhoneNumber] = useState<string | null>(
    null,
  );
  const [locationCustom, setLocationCustom] = useState<string | null>(null);
  const [isShowResendConfirm, setIsShowResendConfirm] = useState<boolean>(
    false,
  );

  const isEventTypeEvent = Boolean(props.routineEvent?.eventType);

  const initialSelectedUsers = [
    {
      uid: props.viewer.uid,
      name: props.viewer.name,
      profileImage: props.viewer.profileImage,
      role: Role.Admin,
      userType: UserType.Host,
    },
  ];
  const [selectedTeamUsers, setSelectedTeamUsers] = useState<
    (EventTypeUserListItem | string)[]
  >(initialSelectedUsers);

  const [schedules, setSchedules] = useState<ScheduleFormData[][]>([]);
  const isTask = behavior === Behavior.Work;

  const routineScheduleForms = schedules.map((schedule, index) =>
    useRoutineScheduleForm({
      startedAt: startedAt,
      isAllday: isAllday,
      schedule: schedule,
      index: index,
      onSubmit: onSubmitSchedule,
    }),
  );

  // useEffect(() => {
  //   if (props.routineEvent) return;

  //   // 1時間後を初期値としてセット
  //   if (!startedAt) {
  //     let newStartedAt = getDateTime({}, timezone);
  //     newStartedAt = newStartedAt
  //       .plus({ hour: 1 })
  //       .plus({
  //         minutes: minuteStep - (newStartedAt.minute % minuteStep),
  //       })
  //       .startOf('minute');
  //     setStartedAt(newStartedAt);
  //     for (const routineScheduleForm of routineScheduleForms) {
  //       routineScheduleForm.actions.onChangeStartedAt(newStartedAt);
  //     }
  //   }
  // }, []);

  useEffect(() => {
    if (!props.isPopoverOpen) return;

    nameInputRef.current?.focus();
  }, [props.isPopoverOpen]);

  useEffect(() => {
    if (!props.routineEvent) return;
    const routine = props.routineEvent.routine;

    setName(props.routineEvent?.name || name);
    setDescription(props.routineEvent?.description || description);
    setProjectId(props.routineEvent?.projectId || projectId);

    setIsRepeat(!!routine);
    setDuration(routine?.duration || duration);
    const newSchedules = routine
      ? convertToScheduleFormData(routine.schedules)
      : schedules;
    setSchedules(newSchedules);
    setWorkflows(routine?.workflows || workflows);
    setSourceType(props.routineEvent?.sourceType || null);
    setHasWorkflows(
      !!routine &&
        routine.workflows &&
        Array.isArray(routine.workflows) &&
        routine.workflows.length > 0,
    );
    setLinkType(routine?.linkType || linkType);
    setCustomLink(routine?.customLink || customLink);
    setTransparency(props.routineEvent.transparency || transparency);

    setLocationType(props.routineEvent?.locationId || locationType);
    setLocationAddress(props.routineEvent?.address || locationAddress);
    setLocationAccess(props.routineEvent?.access || locationAccess);
    setLocationPhoneNumber(
      props.routineEvent?.phoneNumber || locationPhoneNumber,
    );
    setLocationCustom(props.routineEvent?.customLocation || locationCustom);

    setSelectedTeamUsers(props.routineEvent?.users || selectedTeamUsers);

    setIsRemind(props.routineEvent?.isRemind ?? isRemind);
    // setSlackChannel(
    //   props.routineEvent.slackChannel || routine?.slackChannel || slackChannel,
    // );

    const newTimezone = props.routineEvent?.timeZone || timezone;
    setTimezone(newTimezone);

    let newVisibility = visibility;
    if (
      props.routineEvent.displayType === DisplayType.TeamPublic &&
      props.routineEvent.visibilityType === VisibilityType.Busy
    ) {
      newVisibility = DisplayType.TeamPublic;
    } else if (
      props.routineEvent.displayType === DisplayType.Private &&
      props.routineEvent.visibilityType === VisibilityType.Busy
    ) {
      newVisibility = DisplayType.Private;
    }
    setVisibility(newVisibility);

    const newIsAllday = props.routineEvent.isAllDay || isAllday;
    setIsAllday(newIsAllday);

    let newStartedAt = startedAt;
    let newEndedAt = endedAt;
    if (routine) {
      newStartedAt = getDateTime(routine.startedAt, newTimezone);
      newEndedAt = newStartedAt.plus({
        minute: routine.duration,
      });
      setEndDate(routine.endedAt ? 1 : 0);
      setEndDateSpecified(
        routine.endedAt ? getDateTime(routine.endedAt) : null,
      );
    } else {
      newStartedAt = getDateTime(props.routineEvent?.startedAt, newTimezone);
      newEndedAt = getDateTime(props.routineEvent?.endedAt, newTimezone);
    }
    setStartedAt(newStartedAt);
    setEndedAt(newEndedAt);

    setPriority(props.routineEvent.priority);
    setTag(props.routineEvent.label);

    setBehavior(props.routineEvent.behavior || Behavior.Event);

    setIsEditReady(true);

    return () => {};
  }, [props.routineEvent]);

  useEffect(() => {
    if (startedAt && endedAt) {
      const newDuration = getDateTime(endedAt, timezone).diff(
        getDateTime(startedAt, timezone),
        'minutes',
      ).minutes;
      setDuration(newDuration);
    } else if (startedAt && !endedAt) {
      setEndedAt(startedAt.plus({ hours: 1 }));
    } else if (!startedAt && endedAt) {
      setStartedAt(endedAt.minus({ hours: 1 }));
    }

    // 終日以外の時の最後の選択を記憶
    if (!isAllday) {
      setLastStartedAt(startedAt);
      setLastEndedAt(endedAt);
    }
  }, [startedAt, endedAt]);

  // useEffect(() => {
  //   // setStartedAt(props.startedAt);
  //   // setEndedAt(props.endedAt);
  //   // setIsAllday(props.isAllDay);

  //   // 日付選択にユーザが紐づいていたらそのユーザを、
  //   // 週&3日表示のblockなら表示選択中のユーザをメンバーにセット
  //   // if (calendar.state.dates[0].userUid) {
  //   //   userIds = [calendar.state.dates[0].userUid];
  //   // } else if (
  //   //   props.viewType === EventCreateViewType.Block &&
  //   //   (calendar.state.calendarUnit === 'week' ||
  //   //     calendar.state.calendarUnit === '3day')
  //   // ) {
  //   //   userIds = props.selectedUserIds;
  //   // }
  //   if (props.selectedUserIds.length > 0) {
  //     const selectedTeamUsers = (props.teamUsers ?? []).filter((user) =>
  //       props.selectedUserIds.includes(user.userId),
  //     );
  //     if (selectedTeamUsers.length > 0) {
  //       setSelectedTeamUsers(
  //         selectedTeamUsers.map((user) => ({
  //           uid: user.userId,
  //           name: user.name,
  //           profileImage: user.profileImage,
  //           role: Role.Admin,
  //           userType: UserType.Host,
  //         })),
  //       );
  //     }
  //   }
  // }, [props.selectedUserIds]);

  const router = useRouter();

  const timezoneRequest = useRequest(apiPath('/v1/time_zones'), {
    revalidateOnFocus: false,
  });
  // const slackChannels = useRequest(apiPath(`/v1/integrations/slack/channels`));

  const timezoneOptions = useMemo(() => {
    if (!timezoneRequest.data) return [];

    const timezones: TimeZoneListItem[] = timezoneRequest.data || [];
    return timezones.map((t) => ({
      text: t.timeName,
      value: t.name,
    }));
  }, [timezoneRequest.data]);

  const locationList = [LocationType.InPerson];
  if (props.userApp?.isZoom === true) {
    locationList.push(LocationType.Zoom);
  }
  if (props.userApp?.isGoogle === true) {
    locationList.push(LocationType.GoogleMeet);
  }
  if (props.userApp?.isTeams === true) {
    locationList.push(LocationType.Teams);
  }
  locationList.push(LocationType.Phone, LocationType.Custom);

  const projectOptions = useMemo(() => {
    return [
      { text: t('project unspecified'), value: null },
      ...dashboardState.projects.map((p) => ({
        text: p.name,
        value: p.id,
      })),
    ];
  }, [dashboardState.projects]);

  let linkTypeOptions = [
    { value: LinkType.Default, text: t('Link Routine Home') },
    { value: LinkType.ProjectHome, text: t('Link Project Home') },
    { value: LinkType.Custom, text: t('Link Custom') },
  ];
  if (!projectId) {
    linkTypeOptions = linkTypeOptions.filter(
      (o) => o.value !== LinkType.ProjectHome,
    );
  }

  function onChange(e: SyntheticEvent<HTMLInputElement>) {
    const { name, value } = e.currentTarget;
    const newErrors = { ...errors };
    switch (name) {
      case 'name':
        setName(value);
        if (state.creatingEvent) {
          dispatch(setCreatingEvent({ ...state.creatingEvent, name: value }));
        }
        if (!value) {
          newErrors['name'] = [t('required')];
        } else {
          delete newErrors['name'];
        }
        setErrors(newErrors);
        break;
      case 'isRemind':
        setIsRemind(Number(value) === 1 ? true : false);
        if (!value) {
          newErrors['isRemind'] = [t('required')];
        } else {
          delete newErrors['isRemind'];
        }
        setErrors(newErrors);
        break;
      case 'customLink':
        setCustomLink(value);
        break;
      // case 'slackChannel':
      //   setSlackChannel(value || null);
      //   break;
      case 'linkType':
        setLinkType(Number(value));
        break;
      case 'timezone':
        setTimezone(value);
        if (startedAt) {
          setStartedAt(startedAt.setZone(value));
        }
        if (endedAt) {
          setEndedAt(endedAt.setZone(value));
        }
        break;
      case 'project':
        setProjectId(Number(value));
        if (
          (value === null || value === '') &&
          linkType === LinkType.ProjectHome
        ) {
          setLinkType(LinkType.Default);
        }
        break;
      case 'description':
        delete newErrors['description'];
        setDescription(value || null);
        setErrors(newErrors);
        break;
      case 'locationAddress':
        delete newErrors['address'];
        setLocationAddress(value || null);
        break;
      case 'locationAccess':
        delete newErrors['access'];
        setLocationAccess(value || null);
        break;
      case 'locationPhoneNumber':
        delete newErrors['phoneNumber'];
        setLocationPhoneNumber(value || null);
        break;
      case 'locationCustom':
        delete newErrors['customLocation'];
        setLocationCustom(value || null);
        break;
      case 'allday':
        if (!startedAt || !endedAt) return;
        const allday = !isAllday;
        const now = getDateTime().setZone(state.timeZone);
        setIsAllday(allday);

        let newStartedAt = null;
        let newEndedAt = null;
        if (allday) {
          newStartedAt = startedAt.startOf('day');
          newEndedAt = endedAt.plus({ day: 1 }).startOf('day');
        } else {
          if (lastStartedAt && lastEndedAt) {
            newStartedAt = lastStartedAt;
            newEndedAt = lastEndedAt;
          } else {
            newStartedAt = now.plus({ hour: 1 }).set({ minute: 0, second: 0 });
            newEndedAt = now.plus({ hour: 2 }).set({ minute: 0, second: 0 });
          }
        }
        setStartedAt(newStartedAt);
        setEndedAt(newEndedAt);
        setErrors(newErrors);
        if (state.creatingEvent) {
          dispatch(
            setCreatingEvent({
              ...state.creatingEvent,
              startedAt: newStartedAt.toISO(),
              endedAt: newEndedAt.toISO(),
              isAllDay: allday,
            }),
          );
        }
        break;
      case 'isRepeat':
        const newValue = Number(value) === 1 ? true : false;
        setIsRepeat(newValue);
        if (newValue) {
          let newStartedAt: DateTime;
          if (!startedAt) {
            newStartedAt = getDateTime({}, timezone);
            setStartedAt(newStartedAt);
          } else {
            newStartedAt = getDateTime(startedAt, timezone);
          }
          schedules.length === 0 && onClickAddSchedule(newStartedAt);
        }
        // calendar.dispatch(
        //   setDate(
        //     {
        //       ...calendar.state.dates[0],
        //     },
        //     0,
        //   ),
        // );
        break;
      case 'transparency':
        setTransparency(Number(value));
        break;
      case 'endDate':
        setEndDate(Number(value));
        break;
      default:
        break;
    }
    setErrors(newErrors);
  }

  function onChangePriority(value: string | number | null) {
    setPriority(Number(value));
  }

  function onChangeTag(value: string | number | null) {
    setTag(Number(value));
  }

  function onChangeIsTask(e: SyntheticEvent<HTMLDivElement>) {
    const isToTask = behavior === Behavior.Event;
    setBehavior(isToTask ? Behavior.Work : Behavior.Event);
    setTransparency(isToTask ? Transparency.Transparent : Transparency.Opaque);
    if (!name) {
      nameInputRef.current?.focus();
    }
  }

  function onChangeVisibility(value: string | number | null) {
    setVisibility(Number(value));
  }

  function onClickAddSchedule(date?: DateTime) {
    const newSchedules = [...schedules];

    if (date) {
      newSchedules.push([
        defaultScheduleItem(date.weekday === 7 ? 0 : date.weekday, date),
      ]);
    } else if (startedAt) {
      newSchedules.push([
        defaultScheduleItem(
          startedAt.weekday === 7 ? 0 : startedAt.weekday,
          startedAt,
        ),
      ]);
    } else {
      newSchedules.push([
        defaultScheduleItem(1, startedAt),
        defaultScheduleItem(2, startedAt),
        defaultScheduleItem(3, startedAt),
        defaultScheduleItem(4, startedAt),
        defaultScheduleItem(5, startedAt),
      ]);
    }

    setSchedules(newSchedules);
  }

  function onSubmitSchedule(schedule: ScheduleFormData[], index: number) {
    const newSchedules = [...schedules];
    newSchedules[index] = schedule;
    setSchedules(newSchedules);
  }

  function onDeleteSchedule(index: number) {
    const newSchedules = [...schedules];
    newSchedules.splice(index, 1);
    setSchedules(newSchedules);
  }

  function onChangeWorkflow(workflows: Workflow[]) {
    setWorkflows(workflows);
  }

  const onSubmitForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (
      isScheduleChanged() ||
      isHostChanged() ||
      isGuestChanged() ||
      isNameChanged()
    ) {
      setIsShowResendConfirm(true);
    } else {
      onSubmit(e, false);
    }
  };

  const onCloseResendConfirmDialog = () => {
    setIsShowResendConfirm(false);
  };

  const isScheduleChanged = () => {
    if (
      props.routineEvent &&
      (formatToSeconds(startedAt) !==
        formatToSeconds(getDateTime(props.routineEvent.startedAt)) ||
        formatToSeconds(endedAt) !==
          formatToSeconds(getDateTime(props.routineEvent.endedAt)))
    ) {
      return true;
    }
    return false;
  };

  const isHostChanged = () => {
    if (props.routineEvent) {
      const previousHost = props.routineEvent.users.find(
        (u) => u.role === Role.Admin,
      );
      const newHost = selectedTeamUsers.find(
        (u) => typeof u !== 'string' && u.role === Role.Admin,
      );
      if (typeof newHost !== 'string' && previousHost?.uid !== newHost?.uid) {
        return true;
      }
    }
    return false;
  };

  const isGuestChanged = () => {
    if (props.routineEvent) {
      const previousGuests: string[] = props.routineEvent.users
        .filter((u) => u.role === Role.Editor || u.role === Role.Viewer)
        .map((u) => u.uid);
      const newGuests: string[] = [];
      selectedTeamUsers.forEach((u) => {
        if (
          typeof u !== 'string' &&
          (u.role === Role.Editor || u.role === Role.Viewer)
        ) {
          if (typeof u !== 'string') {
            newGuests.push(u.uid);
          }
        }
      });

      if (previousGuests.length !== newGuests.length) {
        return true;
      }

      previousGuests.sort();
      newGuests.sort();
      for (let i = 0; i < previousGuests.length; i++) {
        if (previousGuests[i] !== newGuests[i]) {
          return true;
        }
      }
    }
    return false;
  };

  const isNameChanged = () => {
    if (props.routineEvent) {
      if (props.routineEvent.name !== name) {
        return true;
      }
    }
    return false;
  };

  async function onSubmit(
    e: SyntheticEvent,
    isSendUpdates: boolean,
    updateMessage?: string,
  ) {
    if (!startedAt) {
      return false;
    }
    if (!endedAt) {
      return false;
    }
    if (!name) {
      return false;
    }
    if (!linkType) {
      return false;
    }
    let visibilityType = VisibilityType.Default;
    let displayType = visibility;
    if (visibility === DisplayType.Public) {
      displayType = DisplayType.TeamPublic; // 202107 EventのDisplay.Publicは設定できるがTeamPublicと同義なので寄せる
    } else {
      visibilityType = VisibilityType.Busy;
    }

    const utcSchedules = convertToUtcSchedules(schedules, timezone);
    // 終日の終了日時は23:59にしているが、APIの仕様に合わせて翌日0:00に変更
    if (props.routineEvent) {
      const updateFields: EventUpdateField[] = [];
      if (isScheduleChanged()) {
        updateFields.push('schedule');
      }
      if (isHostChanged()) {
        updateFields.push('host');
      }
      if (isNameChanged()) {
        updateFields.push('name');
      }

      const hosts = [];
      for (const user of selectedTeamUsers) {
        if (typeof user === 'string' || user.userType !== UserType.Host) {
          continue;
        }
        hosts.push({
          userId: user.uid,
          role: convertRoleToText(user.role),
        });
      }

      const previousAdmins = (props.routineEvent?.users ?? []).filter(
        (u) => u.role === Role.Admin,
      );
      const newAdmins = hosts.filter((h) => h.role === RoleText.Admin);
      // Adminが1人の予約を2名以上にはできない(歴史的経緯で元からAdminが複数いるEventは許容する)
      if (newAdmins.length > 1 && previousAdmins.length === 1) {
        dashboardDispatch(setErrorMessage(t('MultipleHostError')));
        return;
      }

      if (props.routineEvent.eventType) {
        const priorityValue = convertPriorityToText(priority);
        const data: BookingUpdateBody = {
          name,
          description: description ?? undefined,
          startedAt: startedAt.toUTC().toISO(),
          endedAt: endedAt.toUTC().toISO(),
          timeZone: timezone,
          hosts,
          priority: priorityValue,
          isSendUpdates: isSendUpdates ?? false,
          updateMessage,
        };
        setIsLoading(true);
        const result = await patch(
          apiPath(`/v1/bookings/${props.routineEvent.uid}`),
          { data },
        );
        setIsLoading(false);
        if (!result.isSuccess) {
          setErrors(result.errors);
          dashboardDispatch(
            setErrorMessage(translate(getErrorMessage(result))),
          );
          return;
        }
      } else {
        const data: ScheduleUpdateBody = {
          projectId,
          routine: isRepeat
            ? {
                duration: isAllday ? 1440 : duration,
                endedAt:
                  endDate && endDateSpecified
                    ? endDateSpecified.toUTC().toISO()
                    : null,
                schedules: utcSchedules,
              }
            : null,
          name,
          description: description ?? undefined,
          startedAt: startedAt.toUTC().toISO(),
          endedAt: isRepeat ? null : endedAt.toUTC().toISO(),
          isAllDay: isAllday,
          timeZone: timezone,
          location:
            locationType === 'unspecified'
              ? undefined
              : convertLocationTypeToText(locationType),
          phoneNumber:
            locationType === LocationType.Phone ? locationPhoneNumber : null,
          access:
            locationType === LocationType.InPerson ? locationAccess : null,
          address:
            locationType === LocationType.InPerson ? locationAddress : null,
          customLocation:
            locationType === LocationType.Custom ? locationCustom : null,
          attendees: selectedTeamUsers.map((user) => {
            if (typeof user === 'string') {
              return {
                email: user,
                userType: UserTypeText.Guest,
                role: RoleText.Viewer,
              };
            } else {
              return {
                userId: user.uid,
                userType:
                  user.role === Role.Viewer
                    ? UserTypeText.Guest
                    : UserTypeText.Host,
                role: convertRoleToText(user.role),
              };
            }
          }),
          isRemind: isRemind ? true : false,
          displayType: convertDisplayTypeToText(displayType),
          visibilityType: convertVisibilityTypeToText(visibilityType),
          transparency: transparency
            ? convertTransparencyToText(transparency)
            : undefined,
          priority: convertPriorityToText(priority),
          behavior: convertBehaviorToText(behavior),
          isSendUpdates: isSendUpdates ?? false,
          updateMessage,
        };
        setIsLoading(true);
        const result = await patch(
          apiPath(`/v1/schedules/${props.routineEvent.uid}`),
          { data },
        );
        setIsLoading(false);
        if (!result.isSuccess) {
          setErrors(result.errors);
          dashboardDispatch(
            setErrorMessage(translate(getErrorMessage(result))),
          );
          return;
        }
      }

      if (props.onUpdateEvent) {
        props.onUpdateEvent();
      }
    } else {
      const data: ScheduleCreateBody = {
        name,
        startedAt: startedAt.toUTC().toISO(),
        endedAt: isRepeat ? null : endedAt.toUTC().toISO(),
        isAllDay: isAllday,
        timeZone: timezone,
        attendees: selectedTeamUsers.map((user) => {
          if (typeof user === 'string') {
            return {
              email: user,
              role: RoleText.Viewer,
              userType: UserTypeText.Guest,
            };
          } else {
            return {
              userId: user.uid,
              role: convertRoleToText(user.role),
              userType:
                user.role === Role.Viewer
                  ? UserTypeText.Guest
                  : UserTypeText.Host,
            };
          }
        }),
        location:
          locationType === 'unspecified'
            ? null
            : convertLocationTypeToText(locationType),
        phoneNumber:
          locationType === LocationType.Phone ? locationPhoneNumber : null,
        access: locationType === LocationType.InPerson ? locationAccess : null,
        address:
          locationType === LocationType.InPerson ? locationAddress : null,
        customLocation:
          locationType === LocationType.Custom ? locationCustom : null,
        routine: isRepeat
          ? {
              duration: isAllday ? 1440 : duration,
              schedules: utcSchedules,
              endedAt: endDateSpecified?.toUTC().toISO() || null,
            }
          : null,
        isRemind,
        displayType: convertDisplayTypeToText(displayType),
        visibilityType: convertVisibilityTypeToText(visibilityType),
        transparency: convertTransparencyToText(transparency),
        priority: convertPriorityToText(priority),
        behavior: convertBehaviorToText(behavior),
      };
      setIsLoading(true);
      const result = await post(apiPath(`/v1/schedules`), { data });
      setIsLoading(false);
      if (!result.isSuccess) {
        setErrors(result.errors);
        dashboardDispatch(setErrorMessage(translate(getErrorMessage(result))));
        return;
      }

      if (props.onCreateEvent) {
        const event: Event = result.data;
        props.onCreateEvent(event, selectedTeamUsers);
      }
    }
  }

  function onCancel() {
    router.back();
  }

  const onChangeStartedAt = (datetime: DateTime) => {
    if (!datetime.isValid) return;
    const newStartedAt = datetime.startOf('minute');
    setStartedAt(newStartedAt);
    let newEndedAt = endedAt;

    if (startedAt && endedAt) {
      const duration = endedAt.toMillis() - startedAt.toMillis();
      newEndedAt = newStartedAt
        .plus({
          milliseconds: duration,
        })
        .startOf('minute');
      setEndedAt(newEndedAt);
    }

    for (const routineScheduleForm of routineScheduleForms) {
      routineScheduleForm.actions.onChangeStartedAt(newStartedAt);
    }

    if (state.creatingEvent && newStartedAt) {
      if (!isAllday) {
        // let y;
        // let ySpan;
        // if (state.creatingEvent.y && state.creatingEvent.ySpan) {
        //   ySpan =
        //     (state.creatingEvent.endedAt.toMillis() - newStartedAt.toMillis()) /
        //     1000 /
        //     60 /
        //     state.rulerStep;
        //   y = state.creatingEvent.y + (state.creatingEvent.ySpan - ySpan);
        // }
        dispatch(
          setCreatingEvent({
            ...state.creatingEvent,
            startedAt: newStartedAt.toISO(),
            endedAt: newEndedAt
              ? newEndedAt.toISO()
              : state.creatingEvent.endedAt,
            // y,
            // ySpan,
          }),
        );
      }
    }
  };

  const onChangeEndedAt = (datetime: DateTime) => {
    if (!datetime.isValid) return;

    let newEndedAt = datetime.startOf('minute').plus({ day: isAllday ? 1 : 0 });
    if (startedAt && startedAt.toMillis() > newEndedAt.toMillis()) {
      newEndedAt = startedAt
        .plus({
          day: isAllday ? 0 : 1,
        })
        .set({ hour: datetime.hour, minute: datetime.minute })
        .startOf('minute');
    }
    setEndedAt(newEndedAt);
    if (state.creatingEvent && newEndedAt) {
      // let ySpan;
      // if (state.creatingEvent.ySpan) {
      //   ySpan =
      //     (newEndedAt.toMillis() - state.creatingEvent.startedAt.toMillis()) /
      //     1000 /
      //     60 /
      //     state.rulerStep;
      // }
      dispatch(
        setCreatingEvent({
          ...state.creatingEvent,
          endedAt: newEndedAt.toISO(),
          // ySpan,
        }),
      );
    }
  };

  const onChangeEndDateSpecified = (dateTime: DateTime) => {
    if (!dateTime.isValid) return;
    setEndDateSpecified(dateTime);
  };

  const onChangeSelectedUserRole = (
    e: SyntheticEvent<HTMLInputElement>,
    index: number,
  ) => {
    e.preventDefault();
    setSelectedTeamUsers(
      selectedTeamUsers.map((u, i) => {
        return i === index && typeof u !== 'string'
          ? { ...u, role: Number(e.currentTarget.value) }
          : u;
      }),
    );
  };

  const onChangeSelectedEventUserRole = (
    e: SyntheticEvent<HTMLInputElement>,
    user: EventTypeUserListItem,
  ) => {
    e.preventDefault();
    if (typeof user === 'string') return;
    const newData = selectedTeamUsers.map((u) => {
      return typeof u !== 'string' &&
        u.uid === user.uid &&
        u.userType === UserType.Host
        ? { ...u, role: Number(e.currentTarget.value) }
        : u;
    });

    setSelectedTeamUsers(newData);
  };

  const onDeleteSelectedUser = (index: number) => {
    const newSelectedTeamUsers = selectedTeamUsers.filter(
      (_, i) => i !== index,
    );
    setSelectedTeamUsers(newSelectedTeamUsers);
  };

  const onDeleteSelectedEventUser = (user: EventTypeUserListItem) => {
    const newSelectedTeamUsers = selectedTeamUsers.filter((u) => {
      if (
        typeof u === 'string' ||
        (u.uid === user.uid && u.userType === UserType.Host)
      ) {
        return false;
      }
      return true;
    });

    setSelectedTeamUsers(newSelectedTeamUsers);
  };

  // オプションを選択した時に呼ばれるcallback
  const onAddNewUser = (eventValue: TeamUserListItem | string) => {
    if (!eventValue) return;

    const newErrors = { ...errors };
    delete newErrors['selectUsers'];

    if (typeof eventValue === 'string') {
      const email = eventValue;

      const selectedTeamUser = (props.teamUsers || []).find(
        (teamUser) => teamUser.email === email,
      );

      const isAlreadyAdded = selectedTeamUsers.some((selectedUser) => {
        if (typeof selectedUser === 'string') {
          return selectedUser === email;
        } else {
          return selectedUser.uid === selectedTeamUser?.userId;
        }
      });

      if (
        selectedTeamUser &&
        !selectedTeamUsers.some(
          (user) =>
            typeof user !== 'string' && user.uid === selectedTeamUser.userId,
        )
      ) {
        // 追加しようとしているメアドのユーザがチームメンバーにいるならその人を追加する
        setSelectedTeamUsers([
          ...selectedTeamUsers,
          {
            uid: selectedTeamUser.userId,
            name: selectedTeamUser.name,
            profileImage: selectedTeamUser.profileImage,
            role: Role.Viewer,
            userType: UserType.Guest,
          },
        ]);
      } else if (!isEmail(email)) {
        newErrors['selectUsers'] = [t('wrong email format')];
      } else if (isAlreadyAdded) {
        newErrors['selectUsers'] = [t('already added email')];
      } else {
        setSelectedTeamUsers([...selectedTeamUsers, email]);
      }
    } else if (eventValue.userId) {
      const newSelectedTeamUsers = [
        ...selectedTeamUsers,
        {
          uid: eventValue.userId,
          name: eventValue.name,
          profileImage: eventValue.profileImage,
          role: Role.Viewer,
          userType: isEventTypeEvent ? UserType.Host : UserType.Guest,
        },
      ];
      setSelectedTeamUsers(newSelectedTeamUsers);
    }

    setErrors(newErrors);
  };

  const onChangeLocation = (e: React.ChangeEvent<{ value: unknown }>) => {
    e.preventDefault();
    setLocationType(
      e.target.value === 'unspecified'
        ? 'unspecified'
        : (e.target.value as LocationType),
    );
  };

  const isExistError = Object.keys(errors).length !== 0;
  const isFillRequired = !!(name && startedAt && endedAt && linkType);
  const containsAdmin = selectedTeamUsers.some(
    (u) => typeof u !== 'string' && u.role === Role.Admin,
  );
  const canSubmit = !isExistError && isFillRequired && containsAdmin;

  const selectedTeamUserIds: string[] = [];
  selectedTeamUsers.forEach((u) => {
    if (typeof u === 'string') {
      return;
    } else {
      selectedTeamUserIds.push(u.uid);
    }
  });
  return {
    state: {
      isLoading,
      name,
      projectId,
      visibility,
      isRemind,
      linkType,
      customLink,
      duration,
      // slackChannel,
      // slackChannels: (slackChannels.data as SlackChannelListItem[]) || [],
      errors,
      canSubmit,
      workflows,
      hasWorkflows,
      linkTypeOptions,
      schedules,
      isAllday,
      startedAt,
      endedAt,
      isRepeat,
      timezone,
      timezoneOptions,
      projectOptions,
      description,
      selectedTeamUsers,
      selectedTeamUserIds,
      locationList,
      locationType,
      locationAddress,
      locationAccess,
      locationPhoneNumber,
      locationCustom,
      transparency,
      priority,
      tag,
      endDate,
      endDateSpecified,
      nameInputRef,
      isTask,
      isEventTypeEvent,
      isShowResendConfirm,
      sourceType,
    },
    actions: {
      onChange,
      onSubmit,
      onSubmitForm,
      onCancel,
      onChangeStartedAt,
      onChangeEndedAt,
      onChangeWorkflow,
      onSubmitSchedule,
      onDeleteSchedule,
      onChangeSelectedUserRole,
      onChangeSelectedEventUserRole,
      onDeleteSelectedUser,
      onDeleteSelectedEventUser,
      onAddNewUser,
      onChangeLocation,
      onChangePriority,
      onChangeTag,
      onChangeVisibility,
      onChangeEndDateSpecified,
      onChangeIsTask,
      onCloseResendConfirmDialog,
    },
    routineScheduleForms,
  };
};
